(function () {
  const tideCards = document.querySelectorAll('[data-cfsa-tide]');
  if (!tideCards.length) return;

  /* ------------------------------------------------------------------ *
   * Station registry
   *
   * NOAA publishes two kinds of tide prediction stations. Harmonic
   * stations ("R") support 15-minute interval predictions, which is what
   * the 24-hour curve needs. Subordinate stations ("S") only publish
   * high/low events, so `curve: false` tells the UI to expect no series.
   * ------------------------------------------------------------------ */

  const STATION_TIME_ZONE = 'America/New_York';

  const TIDE_STATIONS = [
    { id: '8720218', name: 'Mayport (Bar Pilot Dock)', latitude: 30.3982, longitude: -81.4279, curve: true },
    { id: '8720503', name: 'Red Bay Point, St. Johns River', latitude: 29.9817, longitude: -81.6342, curve: true },
    { id: '8720587', name: 'St. Augustine Beach', latitude: 29.8567, longitude: -81.2633, curve: true },
    { id: '8720625', name: 'Racy Point, St. Johns River', latitude: 29.8017, longitude: -81.5483, curve: true },
    { id: '8720757', name: 'Bings Landing, Matanzas River', latitude: 29.615, longitude: -81.205, curve: true },
    { id: '8720767', name: 'Buffalo Bluff', latitude: 29.595, longitude: -81.6817, curve: true },
    { id: '8720774', name: 'Palatka', latitude: 29.6433, longitude: -81.6317, curve: true },
    { id: '8720793', name: 'Piney Bluff Landing', latitude: 29.5583, longitude: -81.5733, curve: true },
    { id: '8720832', name: 'Welaka', latitude: 29.4767, longitude: -81.675, curve: true },
    { id: '8720954', name: 'Ormond Beach, Halifax River', latitude: 29.285, longitude: -81.0533, curve: false },
    { id: '8721120', name: 'Daytona Beach Shores, Sunglow Pier', latitude: 29.1467, longitude: -80.9633, curve: false },
    { id: '8721147', name: 'Ponce de Leon Inlet', latitude: 29.0633, longitude: -80.9158, curve: true },
    { id: '8721164', name: 'New Smyrna Beach', latitude: 29.0233, longitude: -80.9183, curve: true },
    { id: '8721222', name: 'Packwood Place, Mosquito Lagoon', latitude: 28.94, longitude: -80.87, curve: true },
    { id: '8721223', name: 'Turtle Mound, Mosquito Lagoon', latitude: 28.9267, longitude: -80.825, curve: true },
    { id: '8721604', name: 'Port Canaveral (Trident Pier)', latitude: 28.4158, longitude: -80.5931, curve: true },
    { id: '8721649', name: 'Cocoa Beach', latitude: 28.3683, longitude: -80.6, curve: true },
    { id: '8721804', name: 'Canova Beach', latitude: 28.1383, longitude: -80.5783, curve: false },
    { id: '8721843', name: 'Melbourne Causeway', latitude: 28.0833, longitude: -80.5917, curve: true },
    { id: '8721994', name: 'Micco', latitude: 27.8733, longitude: -80.4967, curve: false },
    { id: '8722004', name: 'Sebastian Inlet bridge', latitude: 27.86, longitude: -80.4483, curve: false },
  ];

  const STATIONS_BY_ID = new Map(TIDE_STATIONS.map((station) => [station.id, station]));

  const NON_TIDAL_NOTE = 'NOAA does not publish tide predictions for this inland freshwater spot.';

  /**
   * Explicit overrides for the section's fishing presets. These win over the
   * nearest-station search so a location can be pinned to the station that is
   * hydrologically correct rather than merely closest in a straight line.
   * `null` means the location has no meaningful tide.
   */
  const LOCATION_STATIONS = {
    'port canaveral': '8721604',
    'canaveral bight': '8721649',
    'banana river': '8721649',
    'indian river': '8721649',
    'mosquito lagoon': '8721223',
    'ponce inlet': '8721147',
    'new smyrna beach': '8721164',
    titusville: '8721604',
    'lake george': '8720832',
    'lake tohopekaliga': null,
    'st. johns river': null,
    'st johns river': null,
  };

  // Beyond this a "nearest" station stops describing the same body of water.
  const MAX_STATION_DISTANCE_MILES = 40;

  // A harmonic station slightly farther away beats a closer subordinate one,
  // because only the harmonic station can draw the curve.
  const CURVE_PREFERENCE_MILES = 8;

  const toRadians = (degrees) => (degrees * Math.PI) / 180;

  const distanceInMiles = (from, to) => {
    const earthRadius = 3958.8;
    const deltaLat = toRadians(to.latitude - from.latitude);
    const deltaLon = toRadians(to.longitude - from.longitude);
    const haversine =
      Math.sin(deltaLat / 2) ** 2 +
      Math.cos(toRadians(from.latitude)) * Math.cos(toRadians(to.latitude)) * Math.sin(deltaLon / 2) ** 2;

    return 2 * earthRadius * Math.asin(Math.sqrt(haversine));
  };

  const withDistance = (station, coordinates) => ({
    ...station,
    timeZone: STATION_TIME_ZONE,
    miles: distanceInMiles(coordinates, station),
  });

  const resolveStation = ({ latitude, longitude, locationName }) => {
    const key = String(locationName || '').trim().toLowerCase();

    if (Object.prototype.hasOwnProperty.call(LOCATION_STATIONS, key)) {
      const stationId = LOCATION_STATIONS[key];
      if (!stationId) return { station: null, reason: NON_TIDAL_NOTE };

      const station = STATIONS_BY_ID.get(stationId);
      if (station) {
        return {
          station: Number.isFinite(latitude) && Number.isFinite(longitude)
            ? withDistance(station, { latitude, longitude })
            : { ...station, timeZone: STATION_TIME_ZONE, miles: null },
        };
      }
    }

    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      return { station: null, reason: 'Choose a location to load tide predictions.' };
    }

    const ranked = TIDE_STATIONS.map((station) => withDistance(station, { latitude, longitude })).sort(
      (a, b) => a.miles - b.miles
    );

    const nearest = ranked[0];
    if (!nearest || nearest.miles > MAX_STATION_DISTANCE_MILES) {
      return { station: null, reason: 'No NOAA tide station is close enough to this spot.' };
    }

    if (!nearest.curve) {
      const nearestWithCurve = ranked.find((station) => station.curve);
      if (nearestWithCurve && nearestWithCurve.miles - nearest.miles <= CURVE_PREFERENCE_MILES) {
        return { station: nearestWithCurve };
      }
    }

    return { station: nearest };
  };

  /* ------------------------------------------------------------------ *
   * Time helpers
   *
   * NOAA returns `lst_ldt` timestamps: the station's own wall clock, with
   * daylight saving already applied and no offset attached. Every timestamp
   * here is therefore stored as "wall clock milliseconds" (the clock reading
   * projected onto the UTC axis) so that comparisons and differences stay
   * correct no matter which time zone the visitor's browser is in.
   * ------------------------------------------------------------------ */

  const WALL_CLOCK_PATTERN = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/;

  const parseStationTime = (value) => {
    const match = WALL_CLOCK_PATTERN.exec(String(value || ''));
    if (!match) return null;

    return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]), Number(match[4]), Number(match[5]));
  };

  const getStationClock = (timeZone) => {
    try {
      const parts = {};
      new Intl.DateTimeFormat('en-US', {
        timeZone,
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
        .formatToParts(new Date())
        .forEach((part) => {
          if (part.type !== 'literal') parts[part.type] = part.value;
        });

      const year = Number(parts.year);
      const month = Number(parts.month);
      const day = Number(parts.day);
      const hour = Number(parts.hour) % 24;
      const minute = Number(parts.minute);

      if ([year, month, day, hour, minute].some((value) => !Number.isFinite(value))) throw new Error('bad parts');

      return {
        now: Date.UTC(year, month - 1, day, hour, minute),
        date: `${parts.year}-${parts.month}-${parts.day}`,
      };
    } catch (error) {
      // Fall back to the visitor's own clock rather than dropping the feature.
      const now = new Date();
      const pad = (value) => String(value).padStart(2, '0');
      return {
        now: Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours(), now.getMinutes()),
        date: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`,
      };
    }
  };

  const MINUTE = 60000;
  const HOUR = 60 * MINUTE;

  const formatClockTime = (wallClockMs) =>
    new Intl.DateTimeFormat('en-US', {
      timeZone: 'UTC',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(new Date(wallClockMs));

  const formatHourLabel = (wallClockMs) =>
    new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', hour: 'numeric', hour12: true }).format(
      new Date(wallClockMs)
    );

  const formatWeekday = (wallClockMs) =>
    new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', weekday: 'short' }).format(new Date(wallClockMs));

  const isSameDay = (a, b) => Math.floor(a / 86400000) === Math.floor(b / 86400000);

  /** Adds a weekday prefix once a tide event rolls past midnight. */
  const formatEventTime = (wallClockMs, referenceMs) =>
    isSameDay(wallClockMs, referenceMs)
      ? formatClockTime(wallClockMs)
      : `${formatWeekday(wallClockMs)} ${formatClockTime(wallClockMs)}`;

  const formatFeet = (value) => `${Number(value).toFixed(1)} ft`;

  const formatStationDistance = (miles) => {
    if (!Number.isFinite(miles)) return '';
    if (miles < 0.5) return ' · at this spot';
    return ` · ${miles < 10 ? miles.toFixed(1) : Math.round(miles)} mi away`;
  };

  const formatCountdown = (milliseconds) => {
    if (!Number.isFinite(milliseconds) || milliseconds <= MINUTE) return 'now';

    const totalMinutes = Math.round(milliseconds / MINUTE);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (!hours) return `in ${minutes} min`;
    if (!minutes) return `in ${hours} hr`;
    return `in ${hours} hr ${minutes} min`;
  };

  /* ------------------------------------------------------------------ *
   * Tide interpretation
   * ------------------------------------------------------------------ */

  const NEAR_SLACK_MINUTES = 40;
  const FLAT_SLOPE_FEET_PER_HOUR = 0.08;

  const normalizePayload = (payload) => {
    const events = (payload?.events || [])
      .map((event) => ({
        time: parseStationTime(event.time ?? event.t),
        height: Number(event.height ?? event.v),
        type: String(event.type || '').toLowerCase() === 'h' ? 'high' : String(event.type || '').toLowerCase(),
      }))
      .filter((event) => Number.isFinite(event.time) && Number.isFinite(event.height))
      .map((event) => ({ ...event, type: event.type === 'high' || event.type === 'low' ? event.type : 'low' }))
      .sort((a, b) => a.time - b.time);

    const series = (payload?.series || [])
      .map((point) => ({ time: parseStationTime(point.time ?? point.t), height: Number(point.height ?? point.v) }))
      .filter((point) => Number.isFinite(point.time) && Number.isFinite(point.height))
      .sort((a, b) => a.time - b.time);

    return { events, series, seriesSupported: Boolean(payload?.seriesSupported && series.length > 1) };
  };

  /** Linear interpolation across the 15-minute prediction series. */
  const heightFromSeries = (series, targetMs) => {
    if (!series.length) return null;
    if (targetMs <= series[0].time) return series[0].height;
    if (targetMs >= series[series.length - 1].time) return series[series.length - 1].height;

    for (let index = 1; index < series.length; index += 1) {
      const previous = series[index - 1];
      const next = series[index];
      if (targetMs <= next.time) {
        const span = next.time - previous.time;
        if (!span) return next.height;
        const ratio = (targetMs - previous.time) / span;
        return previous.height + (next.height - previous.height) * ratio;
      }
    }

    return null;
  };

  /**
   * Cosine interpolation between the surrounding high and low. Used when a
   * subordinate station gives us events but no interval series; it tracks a
   * real tide curve far better than a straight line between extremes.
   */
  const heightFromEvents = (events, targetMs) => {
    let previous = null;
    let next = null;

    for (const event of events) {
      if (event.time <= targetMs) previous = event;
      else {
        next = event;
        break;
      }
    }

    if (previous && next) {
      const span = next.time - previous.time;
      if (!span) return next.height;
      const progress = (targetMs - previous.time) / span;
      const middle = (previous.height + next.height) / 2;
      const amplitude = (previous.height - next.height) / 2;
      return middle + amplitude * Math.cos(Math.PI * progress);
    }

    return (previous || next)?.height ?? null;
  };

  const deriveTideState = ({ events, series, seriesSupported }, nowMs) => {
    const upcoming = events.filter((event) => event.time > nowMs);
    const nextEvent = upcoming[0] || null;
    const nextHigh = upcoming.find((event) => event.type === 'high') || null;
    const nextLow = upcoming.find((event) => event.type === 'low') || null;

    const height = seriesSupported ? heightFromSeries(series, nowMs) : heightFromEvents(events, nowMs);

    let slopePerHour = null;
    if (seriesSupported) {
      const before = heightFromSeries(series, nowMs - 30 * MINUTE);
      const after = heightFromSeries(series, nowMs + 30 * MINUTE);
      if (Number.isFinite(before) && Number.isFinite(after)) slopePerHour = after - before;
    }

    // Distance to the closest extreme in either direction decides "slack".
    const closestEvent = events.reduce((closest, event) => {
      const distance = Math.abs(event.time - nowMs);
      return !closest || distance < Math.abs(closest.time - nowMs) ? event : closest;
    }, null);

    const minutesFromExtreme = closestEvent ? Math.abs(closestEvent.time - nowMs) / MINUTE : Infinity;

    let trend = 'unknown';
    if (closestEvent && minutesFromExtreme <= NEAR_SLACK_MINUTES) {
      trend = closestEvent.type === 'high' ? 'near-high' : 'near-low';
    } else if (Number.isFinite(slopePerHour) && Math.abs(slopePerHour) > FLAT_SLOPE_FEET_PER_HOUR) {
      trend = slopePerHour > 0 ? 'rising' : 'falling';
    } else if (nextEvent) {
      trend = nextEvent.type === 'high' ? 'rising' : 'falling';
    }

    return {
      height: Number.isFinite(height) ? height : null,
      heightIsEstimate: !seriesSupported,
      trend,
      slopePerHour,
      nextEvent,
      nextHigh,
      nextLow,
      upcoming,
    };
  };

  const TREND_LABELS = {
    rising: 'Rising',
    falling: 'Falling',
    'near-high': 'Near High Tide',
    'near-low': 'Near Low Tide',
    unknown: 'Tide',
  };

  const TREND_ARROWS = { rising: '↑', falling: '↓' };

  /* ------------------------------------------------------------------ *
   * Data access: cached proxy first, NOAA directly as a fallback
   * ------------------------------------------------------------------ */

  const NOAA_ENDPOINT = 'https://api.tidesandcurrents.noaa.gov/api/prod/datagetter';
  const CACHE_TTL = 6 * HOUR;
  const memoryCache = new Map();

  const cacheKeyFor = (stationId, date) => `cfsa-tide:${stationId}:${date}`;

  const readCache = (key) => {
    const cached = memoryCache.get(key);
    if (cached && Date.now() - cached.storedAt < CACHE_TTL) return cached.payload;

    try {
      const raw = window.sessionStorage.getItem(key);
      if (!raw) return null;

      const parsed = JSON.parse(raw);
      if (!parsed || Date.now() - parsed.storedAt >= CACHE_TTL) return null;

      memoryCache.set(key, parsed);
      return parsed.payload;
    } catch (error) {
      return null;
    }
  };

  const writeCache = (key, payload) => {
    const record = { storedAt: Date.now(), payload };
    memoryCache.set(key, record);

    try {
      window.sessionStorage.setItem(key, JSON.stringify(record));
    } catch (error) {
      // Storage can be unavailable (private mode, quota). Memory cache is enough.
    }
  };

  const shiftDate = (isoDate, days) => {
    const [year, month, day] = isoDate.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));
    date.setUTCDate(date.getUTCDate() + days);
    return date.toISOString().slice(0, 10);
  };

  const noaaRequest = async (stationId, date, interval, signal) => {
    const params = new URLSearchParams({
      begin_date: date.replace(/-/g, ''),
      end_date: shiftDate(date, 1).replace(/-/g, ''),
      station: stationId,
      product: 'predictions',
      datum: 'MLLW',
      time_zone: 'lst_ldt',
      units: 'english',
      format: 'json',
      application: 'CFSAFishing',
    });

    if (interval) params.set('interval', interval);

    const response = await fetch(`${NOAA_ENDPOINT}?${params.toString()}`, { signal });
    if (!response.ok) throw new Error(`NOAA responded with ${response.status}`);

    const data = await response.json();
    if (data?.error?.message) return { ok: false, error: String(data.error.message).trim() };
    if (!Array.isArray(data?.predictions)) return { ok: false, error: 'NOAA returned no predictions' };

    return { ok: true, predictions: data.predictions };
  };

  /** Direct-from-browser fallback for when the cached proxy is unreachable. */
  const fetchFromNoaa = async (stationId, date, signal) => {
    const [eventResult, seriesResult] = await Promise.all([
      noaaRequest(stationId, date, 'hilo', signal),
      noaaRequest(stationId, date, '15', signal).catch((error) => ({ ok: false, error: error.message })),
    ]);

    if (!eventResult.ok) throw new Error(eventResult.error || 'NOAA tide predictions unavailable');

    return {
      station: stationId,
      events: eventResult.predictions.map((entry) => ({
        time: entry.t,
        height: Number(entry.v),
        type: String(entry.type || '').toUpperCase() === 'H' ? 'high' : 'low',
      })),
      series: seriesResult.ok
        ? seriesResult.predictions.map((entry) => ({ time: entry.t, height: Number(entry.v) }))
        : [],
      seriesSupported: Boolean(seriesResult.ok),
    };
  };

  const fetchTideData = async ({ endpoint, stationId, date, signal }) => {
    const cacheKey = cacheKeyFor(stationId, date);
    const cached = readCache(cacheKey);
    if (cached) return cached;

    let payload = null;

    if (endpoint) {
      try {
        const url = new URL(endpoint, window.location.href);
        url.searchParams.set('station', stationId);
        url.searchParams.set('date', date);

        const response = await fetch(url.toString(), { signal, headers: { accept: 'application/json' } });
        if (response.ok) payload = await response.json();
      } catch (error) {
        if (error?.name === 'AbortError') throw error;
        // Proxy unavailable: fall through to NOAA directly.
      }
    }

    if (!payload || !Array.isArray(payload.events) || !payload.events.length) {
      payload = await fetchFromNoaa(stationId, date, signal);
    }

    writeCache(cacheKey, payload);
    return payload;
  };

  /* ------------------------------------------------------------------ *
   * 24-hour curve (hand-rolled SVG, no charting dependency)
   * ------------------------------------------------------------------ */

  const WINDOW_LOOKBACK = 3 * HOUR;
  const WINDOW_SPAN = 24 * HOUR;

  /**
   * The chart keeps a fixed aspect ratio so strokes and labels never distort.
   * A narrow container therefore gets its own taller, narrower viewBox instead
   * of shrinking the wide one, which would squash the curve and crowd labels.
   */
  const chartLayout = (containerWidth) => {
    const narrow = containerWidth > 0 && containerWidth < 430;
    const width = narrow ? 440 : 720;

    return {
      width,
      height: narrow ? 300 : 260,
      plotTop: narrow ? 60 : 50,
      plotBottom: narrow ? 222 : 196,
      plotLeft: 16,
      plotRight: width - 16,
      axisY: narrow ? 282 : 248,
      maxPoints: narrow ? 48 : 72,
      labelInset: narrow ? 30 : 26,
    };
  };

  const round = (value) => Math.round(value * 100) / 100;

  const buildSmoothPath = (points) => {
    if (points.length < 2) return '';

    let path = `M ${round(points[0].x)} ${round(points[0].y)}`;

    // Catmull-Rom control points converted to cubic curves, which keeps the
    // tide line continuously smooth instead of visibly segmented.
    for (let index = 0; index < points.length - 1; index += 1) {
      const previous = points[index - 1] || points[index];
      const current = points[index];
      const next = points[index + 1];
      const following = points[index + 2] || next;

      const controlOneX = current.x + (next.x - previous.x) / 6;
      const controlOneY = current.y + (next.y - previous.y) / 6;
      const controlTwoX = next.x - (following.x - current.x) / 6;
      const controlTwoY = next.y - (following.y - current.y) / 6;

      path += ` C ${round(controlOneX)} ${round(controlOneY)}, ${round(controlTwoX)} ${round(controlTwoY)}, ${round(next.x)} ${round(next.y)}`;
    }

    return path;
  };

  const buildTimeTicks = (windowStart, windowEnd) => {
    const ticks = [];
    const cursor = new Date(windowStart);
    cursor.setUTCMinutes(0, 0, 0);

    let time = cursor.getTime();
    while (time <= windowEnd) {
      if (time >= windowStart && new Date(time).getUTCHours() % 6 === 0) ticks.push(time);
      time += HOUR;
    }

    return ticks;
  };

  const escapeMarkup = (value) =>
    String(value).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  const buildCurveSvg = ({ series, events, nowMs, uid, containerWidth = 0 }) => {
    const windowStart = nowMs - WINDOW_LOOKBACK;
    const windowEnd = windowStart + WINDOW_SPAN;

    const windowed = series.filter((point) => point.time >= windowStart && point.time <= windowEnd);
    if (windowed.length < 8) return null;

    const layout = chartLayout(containerWidth);
    const step = Math.max(1, Math.ceil(windowed.length / layout.maxPoints));
    const sampled = windowed.filter((point, index) => index % step === 0 || index === windowed.length - 1);

    const heights = windowed.map((point) => point.height);
    const minHeight = Math.min(...heights);
    const maxHeight = Math.max(...heights);
    const range = maxHeight - minHeight || 1;
    const padded = { min: minHeight - range * 0.12, max: maxHeight + range * 0.12 };

    const xFor = (time) =>
      layout.plotLeft +
      ((time - windowStart) / (windowEnd - windowStart)) * (layout.plotRight - layout.plotLeft);
    const yFor = (height) =>
      layout.plotBottom -
      ((height - padded.min) / (padded.max - padded.min)) * (layout.plotBottom - layout.plotTop);

    const points = sampled.map((point) => ({ x: xFor(point.time), y: yFor(point.height) }));
    const linePath = buildSmoothPath(points);
    if (!linePath) return null;

    const areaPath = `${linePath} L ${round(points[points.length - 1].x)} ${layout.plotBottom} L ${round(points[0].x)} ${layout.plotBottom} Z`;

    const clampLabelX = (x) =>
      Math.min(Math.max(x, layout.plotLeft + layout.labelInset), layout.plotRight - layout.labelInset);

    const markers = events
      .filter((event) => event.time >= windowStart && event.time <= windowEnd)
      .map((event) => {
        const x = xFor(event.time);
        const y = yFor(event.height);
        const labelX = clampLabelX(x);
        const isHigh = event.type === 'high';
        const tagY = isHigh ? y - 26 : y + 21;
        const valueY = isHigh ? y - 12 : y + 35;

        return `
          <g class="cfsa-tide-chart__marker cfsa-tide-chart__marker--${isHigh ? 'high' : 'low'}">
            <circle cx="${round(x)}" cy="${round(y)}" r="4" />
            <text x="${round(labelX)}" y="${round(tagY)}" text-anchor="middle" class="cfsa-tide-chart__tag">${isHigh ? 'HIGH' : 'LOW'}</text>
            <text x="${round(labelX)}" y="${round(valueY)}" text-anchor="middle" class="cfsa-tide-chart__marker-value">${escapeMarkup(formatFeet(event.height))}</text>
          </g>
        `;
      })
      .join('');

    const ticks = buildTimeTicks(windowStart, windowEnd)
      .map((time) => {
        const x = xFor(time);
        return `
          <g class="cfsa-tide-chart__tick">
            <line x1="${round(x)}" y1="${layout.plotTop - 6}" x2="${round(x)}" y2="${layout.plotBottom}" />
            <text x="${round(clampLabelX(x))}" y="${layout.axisY}" text-anchor="middle">${escapeMarkup(formatHourLabel(time))}</text>
          </g>
        `;
      })
      .join('');

    const nowHeight = heightFromSeries(series, nowMs);
    const nowX = xFor(nowMs);
    const nowY = Number.isFinite(nowHeight) ? yFor(nowHeight) : null;

    const nowMarker =
      nowY === null
        ? ''
        : `
          <g class="cfsa-tide-chart__now">
            <line x1="${round(nowX)}" y1="${layout.plotTop - 20}" x2="${round(nowX)}" y2="${layout.plotBottom}" />
            <text x="${round(clampLabelX(nowX))}" y="${layout.plotTop - 28}" text-anchor="middle">NOW</text>
            <circle cx="${round(nowX)}" cy="${round(nowY)}" r="7.5" class="cfsa-tide-chart__now-halo" />
            <circle cx="${round(nowX)}" cy="${round(nowY)}" r="4.5" class="cfsa-tide-chart__now-dot" />
          </g>
        `;

    const gradientId = `cfsaTideFill-${uid}`;

    return `
      <svg class="cfsa-tide-chart" viewBox="0 0 ${layout.width} ${layout.height}" role="img"
        aria-label="Tide height over the next 24 hours" focusable="false">
        <defs>
          <linearGradient id="${gradientId}" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stop-color="var(--cfsa-badge-ink)" stop-opacity="0.28" />
            <stop offset="100%" stop-color="var(--cfsa-badge-ink)" stop-opacity="0.02" />
          </linearGradient>
        </defs>
        <line class="cfsa-tide-chart__baseline" x1="${layout.plotLeft}" y1="${layout.plotBottom}" x2="${layout.plotRight}" y2="${layout.plotBottom}" />
        ${ticks}
        <path class="cfsa-tide-chart__area" d="${areaPath}" fill="url(#${gradientId})" />
        <path class="cfsa-tide-chart__line" d="${linePath}" />
        ${markers}
        ${nowMarker}
      </svg>
    `;
  };

  /* ------------------------------------------------------------------ *
   * Rendering
   * ------------------------------------------------------------------ */

  let uidCounter = 0;

  const setText = (root, selector, value) => {
    const target = root.querySelector(selector);
    if (target) target.textContent = value;
  };

  const setStatus = (card, message, { busy = false } = {}) => {
    const status = card.querySelector('[data-tide-status]');
    const content = card.querySelector('[data-tide-content]');

    if (status) {
      status.hidden = !message;
      status.textContent = message || '';
      status.classList.toggle('is-loading', busy);
    }
    if (content) content.hidden = Boolean(message);
  };

  const renderEvents = (card, upcoming, nowMs) => {
    const list = card.querySelector('[data-tide-events]');
    if (!list) return;

    const items = upcoming.slice(0, 4);
    if (!items.length) {
      list.hidden = true;
      list.innerHTML = '';
      return;
    }

    list.innerHTML = items
      .map((event) => {
        const isHigh = event.type === 'high';
        return `
          <li class="cfsa-tide__event cfsa-tide__event--${isHigh ? 'high' : 'low'}">
            <span class="cfsa-tide__event-arrow" aria-hidden="true">${isHigh ? '↑' : '↓'}</span>
            <span class="cfsa-tide__event-type">${isHigh ? 'High' : 'Low'}</span>
            <span class="cfsa-tide__event-height">${escapeMarkup(formatFeet(event.height))}</span>
            <span class="cfsa-tide__event-time">${escapeMarkup(formatEventTime(event.time, nowMs))}</span>
          </li>
        `;
      })
      .join('');
    list.hidden = false;
  };

  const renderChart = (card, data, nowMs) => {
    const chart = card.querySelector('[data-tide-chart]');
    if (!chart) return;

    if (!data.seriesSupported) {
      chart.hidden = true;
      chart.innerHTML = '';
      return;
    }

    const svg = buildCurveSvg({
      series: data.series,
      events: data.events,
      nowMs,
      uid: card.dataset.tideUid,
      containerWidth: chart.clientWidth || card.clientWidth || 0,
    });

    if (!svg) {
      chart.hidden = true;
      chart.innerHTML = '';
      return;
    }

    chart.innerHTML = svg;
    chart.hidden = false;
  };

  const render = (card) => {
    const context = card.cfsaTide;
    if (!context?.data || !context.station) return;

    const { now } = getStationClock(context.station.timeZone);
    const state = deriveTideState(context.data, now);

    const trendLabel = TREND_LABELS[state.trend] || TREND_LABELS.unknown;
    const arrow = TREND_ARROWS[state.trend] || '';

    setText(card, '[data-tide-trend]', arrow ? `${trendLabel} ${arrow}` : trendLabel);
    setText(card, '[data-tide-height]', state.height === null ? 'Unavailable' : formatFeet(state.height));

    const stationLabel = card.querySelector('[data-tide-station]');
    if (stationLabel) {
      stationLabel.textContent = `${context.station.name}${formatStationDistance(context.station.miles)}`;
    }

    if (state.nextEvent) {
      setText(card, '[data-tide-next-label]', state.nextEvent.type === 'high' ? 'Next High' : 'Next Low');
      setText(
        card,
        '[data-tide-next-value]',
        `${formatFeet(state.nextEvent.height)} · ${formatEventTime(state.nextEvent.time, now)}`
      );
      setText(card, '[data-tide-countdown]', formatCountdown(state.nextEvent.time - now));
    } else {
      setText(card, '[data-tide-next-label]', 'Next tide');
      setText(card, '[data-tide-next-value]', 'Unavailable');
      setText(card, '[data-tide-countdown]', '');
    }

    const note = card.querySelector('[data-tide-note]');
    if (note) {
      note.textContent = state.heightIsEstimate
        ? 'This station reports high and low tides only, so the current height is interpolated.'
        : '';
      note.hidden = !state.heightIsEstimate;
    }

    // Reveal before drawing so the chart can measure its own width and pick the
    // matching viewBox profile.
    setStatus(card, '');
    renderEvents(card, state.upcoming, now);
    renderChart(card, context.data, now);
  };

  const load = async (card, location) => {
    const context = card.cfsaTide;
    const { station, reason } = resolveStation(location);

    context.station = station;

    if (!station) {
      context.data = null;
      setStatus(card, reason || 'Tide predictions are unavailable for this location.');
      return;
    }

    const { date } = getStationClock(station.timeZone);
    context.date = date;

    context.controller?.abort();
    const controller = new AbortController();
    context.controller = controller;

    setStatus(card, 'Loading tide predictions...', { busy: true });

    try {
      const payload = await fetchTideData({
        endpoint: card.dataset.tideEndpoint,
        stationId: station.id,
        date,
        signal: controller.signal,
      });

      if (controller.signal.aborted) return;

      const data = normalizePayload(payload);
      if (!data.events.length) throw new Error('No tide events returned');

      context.data = data;
      render(card);
    } catch (error) {
      if (error?.name === 'AbortError') return;

      context.data = null;
      setStatus(card, 'Tide predictions are temporarily unavailable.');
      console.warn('[CFSA tides]', error);
    }
  };

  const initCard = (card, weatherSection) => {
    if (card.dataset.tideInitialized === 'true') return;
    card.dataset.tideInitialized = 'true';

    uidCounter += 1;
    card.dataset.tideUid = String(uidCounter);
    card.cfsaTide = { data: null, station: null, date: null, controller: null };

    const handleLocation = (detail) => {
      if (!detail) return;

      const location = {
        latitude: Number(detail.latitude),
        longitude: Number(detail.longitude),
        locationName: detail.locationName || '',
      };

      card.cfsaTide.lastLocation = location;
      load(card, location);
    };

    weatherSection?.addEventListener('cfsa:location-change', (event) => handleLocation(event.detail));

    // The weather script may already have resolved a location before this file
    // executed, so pick up whatever it recorded instead of waiting for an event.
    if (weatherSection?.cfsaLocation) handleLocation(weatherSection.cfsaLocation);

    // Keeps "now" honest: re-renders the marker and countdown from cached data,
    // and only refetches when the station's local date rolls over.
    window.setInterval(() => {
      const context = card.cfsaTide;
      if (!context?.data || !context.station) return;

      const { date } = getStationClock(context.station.timeZone);
      if (date !== context.date && context.lastLocation) {
        load(card, context.lastLocation);
        return;
      }

      render(card);
    }, MINUTE);
  };

  tideCards.forEach((card) => initCard(card, card.closest('[data-cfsa-weather]')));

  let resizeTimer = 0;
  window.addEventListener('resize', () => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => {
      tideCards.forEach((card) => {
        if (card.cfsaTide?.data) render(card);
      });
    }, 200);
  });

  // Exposed for debugging and for the offline logic checks in scripts/.
  window.CFSATides = {
    resolveStation,
    deriveTideState,
    normalizePayload,
    buildCurveSvg,
    parseStationTime,
    formatCountdown,
    formatFeet,
    heightFromSeries,
    heightFromEvents,
    TIDE_STATIONS,
    LOCATION_STATIONS,
  };
})();
